import { type JSX, type ReactNode, useEffect, useRef } from "react"

// A <dialog>-backed modal. The browser gives us the focus trap, the backdrop,
// the inert background and Escape; this only bridges React's `open` prop to
// showModal/close and reports dismissals back up.
//
// Matches the plain-HTML modals elsewhere on the site (see modal.scss), so the
// React dialogs and the ERB ones look and behave the same.
export function Modal({
  open,
  title,
  onClose,
  children,
  actions,
  wide,
}: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  actions?: ReactNode
  wide?: boolean
}): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    // Fires for Escape as well as close() — the one place both dismissals meet.
    function onCancelOrClose(): void {
      onClose()
    }

    dialog.addEventListener("close", onCancelOrClose)
    return () => dialog.removeEventListener("close", onCancelOrClose)
  }, [onClose])

  return (
    <dialog
      className={`modal${wide ? " modal--wide" : ""}`}
      ref={ref}
      // The dialog element fills the viewport, so a click landing on it rather
      // than on the panel inside is a click on the backdrop.
      onClick={(event) => {
        if (event.target === ref.current) onClose()
      }}
    >
      <div className="modal__panel">
        <header className="modal__head">
          <h2 className="modal__title">{title}</h2>
          <button type="button" className="modal__close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>

        {children}

        {actions && <div className="modal__actions">{actions}</div>}
      </div>
    </dialog>
  )
}
